import { MemberType } from '../../enum';
import { createTypingMessage, findMessageIndex } from '../../lib';
import { MemberKind, MessageListItem } from '../../types';
import { MessageStoreApi } from './MessageList.store';

interface EnqueueOptions {
  /**
   * Loading user type.
   */
  memberKind?: MemberKind;
  /**
   * If enabled, then this will rethrow any error occurred while loading the
   * message from factory. You can then handle that error via `promise.catch(err)`.
   */
  enableError?: boolean;
  /**
   * This will allow cancel the action performed after the API is completed.
   */
  ignoreSignal?: () => boolean;
}

/**
 * Enqueue a promise message/messages. This will create a typing message and then replace that
 * typing message with the resulting from the promise. This will quickly let you enqueue loading and
 * message from APIs.
 */
export async function addAsync<T extends MessageListItem>(
  store: MessageStoreApi,
  futureMessage: Promise<null | T | T[]> | (() => Promise<null | T | T[]>),
  { memberKind = MemberType.AGENT, enableError = false, ignoreSignal = () => false }: EnqueueOptions = {}
): Promise<T | T[] | null> {
  const typingMessage = createTypingMessage(null, memberKind || MemberType.AGENT);
  store.add(typingMessage);

  const IGNORE_MESSAGE = 'ENQUEUE_IGNORE_MESSAGE';
  const ignoreIfSignaled = (r: T | T[] | null) => (ignoreSignal?.() ? Promise.reject(IGNORE_MESSAGE) : r);

  try {
    const messageOrList = await (typeof futureMessage === 'function' ? futureMessage() : futureMessage).then(
      ignoreIfSignaled
    );
    if (!messageOrList) return null;
    const messageQueue = Array.isArray(messageOrList) ? messageOrList : [messageOrList];
    const idx = findMessageIndex(store.get(), typingMessage);
    store.remove(typingMessage);
    idx >= 0 ? store.addAt(idx, ...messageQueue) : store.add(...messageQueue);
    return messageOrList;
  } catch (err) {
    store.remove(typingMessage);
    if (enableError && err !== IGNORE_MESSAGE) throw err;
  }
  return null;
}

export function removeMessagesAfter(store: MessageStoreApi, item: MessageListItem) {
  const conversation = store.get();
  const anchor = findMessageIndex(conversation, item);
  if (anchor < 0) return;
  if (anchor === 0) store.clear();
  store.remove(...conversation.slice(anchor));
}

export function addIfNotExists(store: MessageStoreApi, ...items: MessageListItem[]) {
  return store.add(...items.filter((i) => findMessageIndex(store.get(), i) === -1));
}
